import { useCallback, useEffect, useState } from 'react';
import { api } from '../../shell/bridge';
import { SkeletonBlock } from '../../ui/Skeleton';
import { Tooltip } from '../../ui/Tooltip';
import { INBOX_LANE_ADDRESSES } from '../../../../shared/inbox-address';

/** A sender's address, reduced to a name a row can print: the local part with
 *  dots, dashes and underscores opened out. The full address lives in the
 *  row's tooltip (card 3hTR4KBBnMLR — Jonathan: "the email addresses should be
 *  in the tooltip", so the face stays a summary). */
export function senderName(address: string): string {
  const local = (address.split('@')[0] || '').replace(/[._-]+/g, ' ').trim();
  if (!local) return address;
  return local.replace(/(^|\s)[a-z]/g, m => m.toUpperCase());
}

/**
 * One row of GET /api/inbox (newest first), trimmed to what this view prints.
 * Shared by the Routing Rules extra view (card YMMXiFOdFgdF) and Life's Admin
 * sub-tab (card tlwgXSkiimS2) — one fetch contract, one rendering, so the two
 * surfaces cannot drift on what the couple is allowed to see.
 */
export interface RoutedItem {
  itemId: string;
  sender: string;
  subject: string;
  snippet: string;
  /** bill | booking | notice | receipt | other */
  category: string;
  /** Why it was classified this way — shown verbatim (PRD §6). */
  categoryReason: string;
  /** both = the pair, single = one named member. */
  route: 'both' | 'single';
  routedTo: string | null;
  /** Why it went where it went — shown verbatim, same contract. */
  routeReason: string;
  quarantined: boolean;
  quarantineReason: string | null;
  receivedAt: number;
  /** Content routing (card 99VNKrT2nwvy): where the concierge filed it, the
   *  sentence that says why, and — when it was too unsure to write — the
   *  surface it suggests instead. */
  filedSurface: 'notebook' | 'memory' | 'idea' | null;
  filedItemId: string | null;
  filedReason: string | null;
  suggestedSurface: 'notebook' | 'memory' | 'idea' | 'inbox' | null;
  suggestedReason: string | null;
  /** Chore intake (card UZDxtHiJhtRt): a parsed $/deadline proposal — the
   *  title it would create, what was read from the message, and whether it
   *  is still waiting on a person. */
  choreProposal: {
    title: string;
    dueAt: number | null;
    freq: 'daily' | 'weekly' | 'monthly' | null;
    amount: number | null;
    currency: string | null;
    reason: string;
    state: 'pending' | 'confirmed' | 'declined';
  } | null;
  choreId: string | null;
  /** Lane (card QLPMbGu3EiXm): `me` = private to its owner, `us` = shared.
   *  A private row never offers a routing change (card NWmSqIsojQyU). */
  lane: 'me' | 'us' | null;
}

interface InboxListProps {
  members: { memberId: string; displayName: string }[];
  /** Renders `data-inbox-state` so a walk can assert the list settled. */
  listId?: string;
}

/** receivedAt is ms since epoch; tolerate a seconds value so the stamp never
 *  falls back to 1970. */
export function whenLabel(ts: number): string {
  if (!Number.isFinite(ts) || ts <= 0) return '';
  const ms = ts < 1e12 ? ts * 1000 : ts;
  return new Date(ms).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * The couple inbox as rows: category + routing reason verbatim, straight from
 * GET /api/inbox. The endpoint is bearer-scoped to the caller's Durable Object,
 * so one couple's mail can never arrive in another's list — this component
 * only ever renders what that call returned.
 */
export function InboxList({ members, listId }: InboxListProps) {
  const [items, setItems] = useState<RoutedItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** itemId currently being moved — one row disables while it waits. */
  const [moving, setMoving] = useState<string | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  /** itemId whose chore proposal is being decided (card UZDxtHiJhtRt). */
  const [deciding, setDeciding] = useState<string | null>(null);

  /* Desktop preview (card DkXOxlTnLPJ8). `previewOpen` is what Esc flips, so
     a closed panel is absent rather than an empty box; `selectedId` names the
     row while open and falls back to the newest message. */
  const [previewOpen, setPreviewOpen] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /* The two-pane hinge: >=1024px the message opens in a right pane, below it
     the detail stays inside the card and the pane is not in the DOM at all —
     so a resize across the hinge moves the detail back into the card instead
     of stranding a fixed drawer (same read-then-keep-current as Notebook). */
  const [viewportW, setViewportW] = useState<number>(() =>
    typeof window === 'undefined' ? 0 : window.innerWidth);
  useEffect(() => {
    const onResize = () => setViewportW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const load = useCallback(async () => {
    setError(null);
    setItems(null);
    try {
      const data = await api<{ items?: RoutedItem[] }>('/api/inbox');
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch {
      setError('The Inbox Could Not Be Loaded.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /* Esc closes the open message: window-level and in the capture phase so it
     works from anywhere on the surface, and it yields to any dialog already
     up (the More drawer, Settings, the tour) — Escape belongs to whichever
     surface the user is actually looking at, never to both. Same rule the
     Notebook context panel ships (card 2wn9seit8zGi). */
  useEffect(() => {
    if (!previewOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('[role="dialog"]')) return;
      setPreviewOpen(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [previewOpen]);

  /** One tap re-files the message (card 99VNKrT2nwvy): the server creates the
   *  entry on the new surface and drops the agent's own previous one, so the
   *  list just reloads with the new verdict. */
  const move = useCallback(async (itemId: string, to: 'notebook' | 'memory' | 'idea') => {
    setMoving(itemId);
    setMoveError(null);
    try {
      await api<{ item?: RoutedItem }>(`/api/inbox/${itemId}/move`, { body: { to } });
      await load();
    } catch (e) {
      // api() throws the JSON error body as the message (bridge.ts).
      const raw = e instanceof Error ? e.message : '';
      let code = '';
      try { code = (JSON.parse(raw) as { error?: { code?: string } })?.error?.code ?? ''; } catch { /* not ours */ }
      setMoveError(
        code === 'no_link_to_file'
          ? 'Your Collection holds links — this message carries none.'
          : 'That Message Could Not Be Moved.',
      );
    } finally {
      setMoving(null);
    }
  }, [load]);

  /** The confirmation gate (card UZDxtHiJhtRt): one tap decides the
   *  proposal. Confirm creates the chore in Life; decline tells the server
   *  this sender misparsed, so it stops asking. */
  const decide = useCallback(async (itemId: string, confirm: boolean) => {
    setDeciding(itemId);
    setMoveError(null);
    try {
      await api(`/api/inbox/${itemId}/${confirm ? 'confirm-chore' : 'decline-chore'}`, {
        body: {},
      });
      await load();
    } catch (e) {
      const raw = e instanceof Error ? e.message : '';
      let code = '';
      try { code = (JSON.parse(raw) as { error?: { code?: string } })?.error?.code ?? ''; } catch { /* not ours */ }
      setMoveError(
        code === 'already_decided' ? 'Someone already decided this one.'
          : code === 'no_pending_proposal' ? 'This message never proposed a chore.'
          : 'That Proposal Could Not Be Decided.',
      );
    } finally {
      setDeciding(null);
    }
  }, [load]);

  /** The four destinations, in the order the app lists them. */
  const SURFACE_NAMES: Record<string, string> = {
    notebook: 'your Notebook',
    memory: 'Memories',
    idea: 'Ideas',
    inbox: 'the Inbox',
  };

  /** Sibling areas — every surface except the one it is already filed to. */
  const siblingsOf = (filed: string | null): Array<'notebook' | 'memory' | 'idea'> =>
    (['notebook', 'memory', 'idea'] as const).filter(s => s !== filed);

  /* ── The two halves of a row (card DkXOxlTnLPJ8) ──────────────────────
     The summary is what the list prints; the detail is everything the card
     says about it — where it went and why, the class and its reason, the
     hold, where the concierge filed it, and the actions. Each is rendered in
     exactly ONE place per width: both inside the card on a phone, or summary
     in the list and detail in the right pane on a wide screen. Nothing is
     duplicated, so a walk can never resolve a tap target to a hidden copy. */
  /** Which of the couple's two addresses this message was sent to (card
   *  AHDZY9Suvr5t): the address IS the routing explanation, but it no longer
   *  sits on the card face — the row prints the sender's name and the
   *  addresses open in its tooltip (card 3hTR4KBBnMLR, Jonathan 2026-11-04).
   *  `lane` is the server's verdict; older rows fall back to the route it
   *  chose. */
  const laneOf = (item: RoutedItem): 'me' | 'us' =>
    item.lane ?? (item.route === 'single' ? 'me' : 'us');

  /** Both addresses, one tap away: where the message came from, which of the
   *  couple's two inboxes it reached, and — on a private row — whose it is.
   *  Card 3hTR4KBBnMLR. */
  const senderTip = (item: RoutedItem): string => {
    const lane = laneOf(item);
    const whose = lane === 'me' && item.routedTo
      ? members.find(m => m.memberId === item.routedTo)?.displayName
      : null;
    return `From ${item.sender} — sent to ${INBOX_LANE_ADDRESSES[lane]}`
      + `${whose ? `, private to ${whose}` : ''}.`;
  };

  const renderSummary = (item: RoutedItem) => (
    <>
      <p className="rr-when">{whenLabel(item.receivedAt)}</p>
      <h3 className="rr-subject">{item.subject || 'No subject'}</h3>
      {/* No audience chip and no address on the face (card 3hTR4KBBnMLR):
          the row names the sender, the tooltip carries both addresses. */}
      <p className="rr-sender" data-inbox-sender={item.sender} data-inbox-to={laneOf(item)}>
        <span className="rr-label">From</span>{' '}
        <Tooltip label={senderTip(item)}>
          <span className="rr-sender-name">{senderName(item.sender)}</span>
        </Tooltip>
      </p>
      {item.snippet && <p className="rr-snippet">{item.snippet}</p>}
    </>
  );

  const renderDetail = (item: RoutedItem) => (
    <>
      {item.quarantined && (
        <p className="rr-held">
          Held for review — {item.quarantineReason || 'rate limit reached'}
        </p>
      )}
      {/* Audience/re-route control removed (card 3hTR4KBBnMLR — Jonathan
          2026-11-04: drop the "Both of you" / "Just <name>" chips and the
          "Sets where the next message …" note). The row is a summary; the
          sender tooltip is the only place the addresses appear. */}

      {/* Content routing (card 99VNKrT2nwvy): where the concierge put
          it, or — when it was too unsure to write — where it suggests.
          Either way the move row is one tap. */}
      {item.filedSurface && (
        <p className="rr-filed" data-filed-surface={item.filedSurface}>
          <span className="rr-label">Filed under</span>
          {SURFACE_NAMES[item.filedSurface] ?? item.filedSurface}
          {item.filedReason ? ` — ${item.filedReason}` : ''}
        </p>
      )}
      {!item.filedSurface && item.suggestedSurface && item.suggestedSurface !== 'inbox' && (
        <p className="rr-suggested" data-suggested-surface={item.suggestedSurface}>
          <span className="rr-label">Suggests</span>
          {SURFACE_NAMES[item.suggestedSurface] ?? item.suggestedSurface}
          {item.suggestedReason ? ` — ${item.suggestedReason}` : ''}
        </p>
      )}
      {/* Chore intake (card UZDxtHiJhtRt): a parsed $/deadline
          proposal waits for a person — confirm lands it in Life,
          decline teaches the server this sender misparsed. */}
      {item.choreProposal && item.choreProposal.state === 'pending' && (
        <div className="rr-chore-proposal" data-proposal-state="pending">
          <p className="rr-filed" data-proposal-title>
            <span className="rr-label">Proposed chore</span>
            {item.choreProposal.title}
          </p>
          <p className="rr-why rr-why-soft">{item.choreProposal.reason}</p>
          <div className="rr-move-row">
            <button
              type="button"
              className="rr-move"
              data-confirm-chore
              disabled={deciding === item.itemId}
              onClick={() => void decide(item.itemId, true)}
            >
              Add to Life
            </button>
            <button
              type="button"
              className="rr-move"
              data-decline-chore
              disabled={deciding === item.itemId}
              onClick={() => void decide(item.itemId, false)}
            >
              Not a chore
            </button>
          </div>
        </div>
      )}
      {item.choreProposal && item.choreProposal.state === 'confirmed' && (
        <p className="rr-filed" data-proposal-state="confirmed">
          <span className="rr-label">Chore added</span>
          {item.choreProposal.title}
        </p>
      )}
      {item.choreProposal && item.choreProposal.state === 'declined' && (
        <p className="rr-filed" data-proposal-state="declined">
          <span className="rr-label">Declined</span>
          {item.choreProposal.reason || 'Not a chore — this sender will not be asked again.'}
        </p>
      )}

      {/* The affordance lives on agent-added items: one the concierge
          filed (siblings = the other two areas), or one it suggested
          but would not write (all three, suggestion first in the DOM). */}
      {!item.quarantined
        && (item.filedSurface
          || (item.suggestedSurface && item.suggestedSurface !== 'inbox')) && (
        <div className="rr-move-row">
          <span className="rr-label">Move to</span>
          {siblingsOf(item.filedSurface).map(surface => (
            <button
              type="button"
              key={surface}
              className="rr-move"
              data-move-to={surface}
              disabled={moving === item.itemId}
              onClick={() => void move(item.itemId, surface)}
            >
              {SURFACE_NAMES[surface]}
            </button>
          ))}
        </div>
      )}
    </>
  );

  /** Pick a row: open the pane and point it there. */
  const select = (itemId: string): void => {
    setSelectedId(itemId);
    setPreviewOpen(true);
  };

  const desktop = viewportW >= 1024;

  /** The pane's content — absent (not empty) once Esc has closed it, and
      pinned to the first row until a row is picked. */
  const previewItem: RoutedItem | null = previewOpen
    ? items?.find(i => i.itemId === selectedId) ?? items?.[0] ?? null
    : null;

  return (
    <div className="rr-list-wrap" id={listId} data-inbox-state={error ? 'error' : items === null ? 'loading' : 'ready'}>
      {error && (
        <div className="rr-state" role="alert">
          <p className="rr-error">{error}</p>
          <button type="button" className="rr-retry" onClick={() => void load()}>
            Try again
          </button>
        </div>
      )}

      {!error && items === null && (
        <div className="rr-state" aria-live="polite">
          <p className="rr-loading-text">Loading your inbox</p>
          <SkeletonBlock lines={4} />
        </div>
      )}

      {!error && items !== null && items.length === 0 && (
        <p className="rr-empty">
          Nothing has come through the inbox yet. The first message lands here with the rule
          that sent it.
        </p>
      )}

      {!error && items !== null && items.length > 0 && (
        <ol className="rr-list">
          {items.map(item => {
            const isSelected =
              previewOpen &&
              (selectedId ? selectedId === item.itemId : items[0]?.itemId === item.itemId);
            return (
              <li
                className="rr-card"
                key={item.itemId}
                data-category={item.category}
                data-selected={desktop && isSelected ? 'true' : undefined}
                onClick={event => {
                  /* On a wide screen the whole row picks the message; a tap on
                     one of the card's OWN controls — a button, or the sender's
                     tooltip trigger (role=button, card 3hTR4KBBnMLR) — must not
                     count as a pick. Without the second guard, tapping a sender
                     name swapped the open pane to that row. */
                  const hit = event.target as HTMLElement;
                  if (hit.closest('button') || hit.closest('[role="button"]')) return;
                  select(item.itemId);
                }}
              >
                {renderSummary(item)}
                {desktop && (
                  <button
                    type="button"
                    className="rr-open-panel"
                    data-open-panel
                    aria-label={`Open ${item.subject || 'this message'}`}
                    aria-pressed={isSelected}
                    onClick={() => select(item.itemId)}
                  >
                    Open
                  </button>
                )}
                {!desktop && renderDetail(item)}
              </li>
            );
          })}
        </ol>
      )}
      {/* The preview pane (card DkXOxlTnLPJ8) is a sibling of the list, not
          part of the list's own conditional: it renders only at >=1024px and
          only while the pane is open, so below the hinge or after Esc it is
          simply not in the DOM. */}
      {desktop && previewItem && (
        <aside className="rr-preview" data-inbox-preview aria-label="Selected message">
          <div className="rr-preview-top">
            <p className="rr-when">{whenLabel(previewItem.receivedAt)}</p>
            <button
              type="button"
              className="rr-close"
              data-close-preview
              onClick={() => setPreviewOpen(false)}
            >
              Close
            </button>
          </div>
          <h3 className="rr-subject">{previewItem.subject || 'No subject'}</h3>
          <p className="rr-sender" data-inbox-sender={previewItem.sender} data-inbox-to={laneOf(previewItem)}>
            <span className="rr-label">From</span>{' '}
            <Tooltip label={senderTip(previewItem)}>
              <span className="rr-sender-name">{senderName(previewItem.sender)}</span>
            </Tooltip>
          </p>
          {previewItem.snippet && <p className="rr-snippet">{previewItem.snippet}</p>}
          {renderDetail(previewItem)}
        </aside>
      )}
      {moveError && (
        <p className="rr-held" role="alert" data-move-error>
          {moveError}
        </p>
      )}
    </div>
  );
}
